<template>
  <div
    id="header"
    ref="headerRef"
    class="lg:h-20 xl:mb-[38px]"
    :class="{
      'pb-8': isMobile && isCheckoutPage && showCheckoutPageNewLayout,
      'sticky z-50': !isCheckoutPage || !showCheckoutPageNewLayout,
    }"
    :style="navigationTop"
  >
    <div
      v-if="isMobile && isCheckoutPage && showCheckoutPageNewLayout"
      class="border-b border-black-10 bg-white p-2"
    >
      <HeaderBenefits />
    </div>
    <div
      ref="headerContentRef"
      class="header-grid-container grid overscroll-none border-b border-black-10 bg-white lg:border-0 lg:bg-gray-header"
    >
      <div
        v-if="
          isMobile ||
          (!isMobile && !(isCheckoutPage && showCheckoutPageNewLayout))
        "
        class="mobile__navigation-container mx-3 gap-x-3"
      >
        <div class="mobile__burger-button-container">
          <Button
            class="mobile__burger-button-container__burger-button"
            type="ghost"
            data-test-id="side-navigation-button"
            @click="openSideNavigationMenu"
          >
            <IconBurger />
            <span
              class="mobile__burger-button-container__burger-button__text leading-none"
            >
              Menu
            </span>
          </Button>
        </div>

        <LogoMobile />

        <div class="container">
          <div class="flex w-full items-center justify-between">
            <LazyTopMetaBar
              v-if="blok && blok.TopMetaBar"
              class="desktop__top-header-meta-bar"
              :blok="blok.TopMetaBar"
            />
            <LazyHeaderMainMenu class="desktop__top-header-main-menu" />
          </div>
        </div>
      </div>

      <div
        v-if="!isMobile && isCheckoutPage && showCheckoutPageNewLayout"
        class="flex items-center bg-white p-4"
      >
        <div class="w-1/5">
          <Logo size="sm" is-checkout />
        </div>
        <div class="w-3/5 self-center">
          <HeaderBenefits
            v-if="!isMobile && isCheckoutPage && showCheckoutPageNewLayout"
          />
        </div>
      </div>

      <div
        v-if="!isMobile && !(isCheckoutPage && showCheckoutPageNewLayout)"
        class="desktop__header-container"
      >
        <div class="container relative px-5">
          <LazyHeaderSubNavigation
            v-if="blok"
            :minified="isHeaderMinified"
            :categories="rootCategories"
            :fetching="fetchingCategories"
            :blok="blok"
            :flyout-menu-category-id="flyoutMenuCategoryId"
            @mouseenter:item="handleMouseEnterItem"
            @mouseleave:navigation="handleMouseLeaveNavigation"
            @click:item="forceCloseFlyout"
          />

          <!-- Eager (not Lazy): the flyout must be in the bundle before the
               first hover. As a Lazy async component its JS chunk loaded on
               demand at hover time, while the sync Backdrop mask painted
               immediately from the same state; a click in that window force-
               closed the flyout mid-mount, orphaning the mask over the page
               (ECOM-808). -->
          <FlyoutMenu
            v-if="activeFlyoutCategory"
            :key="activeFlyoutCategory.id"
            :class="{
              'lg:-mt-2 xl:-mt-5': !isHeaderMinified,
            }"
            :is-open="true"
            :blok="blok"
            :category="activeFlyoutCategory"
            @mouseleave="handleMouseLeaveNavigation"
            @mouseenter="preventClosing"
          />

          <Backdrop
            v-if="hasVisibleFlyout"
            :show="true"
            class="inset-x-0 size-full"
            :inset="false"
          />
        </div>
      </div>
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, computed, watch } from 'vue'
import type { Category } from '@scayle/storefront-nuxt'
import { debounce } from 'es-toolkit'
import {
  findFlyoutNavigationItem,
  hasFlyoutContent,
} from '#layers/base/utils/storyblok'
import { measureChromeOffset } from '#layers/base/utils/sideNavigationLayout'
import useExperiments from '~/composables/useExperiments'
import { useDeviceType } from '~/composables/useDeviceType'
import useUiState from '~/composables/ui/useUiState'
import { useRoute } from '#app/composables/router'
import type { SbNavigation } from '~/cms/storyblok/types/storyblok'
import type { NavigationItem } from '#layers/base/types/navigation'

const props = withDefaults(
  defineProps<{
    rootCategories?: Category[]
    fetchingCategories?: boolean
    forceMinified?: boolean
    blok?: SbNavigation
    stickToBanner?: boolean
  }>(),
  {
    rootCategories: () => [],
    fetchingCategories: false,
    forceMinified: false,
    blok: undefined,
    stickToBanner: false,
  },
)

const route = useRoute()

const {
  isFlyoutMenuOpen,
  closeFlyoutMenu,
  openSideNavigation,
  openFlyoutMenu,
  bannerHeight,
  sideNavigationOffset,
  flyoutMenuCategoryId,
  setFlyoutMenuCategories,
} = useUiState()

const { showCheckoutPageNewLayout } = useExperiments()

const FLYOUT_DELAY = 250

const openFlyoutMenuDebounced = debounce(openFlyoutMenu, FLYOUT_DELAY)

const closeFlyoutMenuDebounced = debounce(closeFlyoutMenu, FLYOUT_DELAY)

const hasNavigationFlyoutContent = (item: Category | NavigationItem) =>
  hasFlyoutContent(findFlyoutNavigationItem(props.blok?.MainNavigation, item))

const { getNavigationItems } = useNavigationMapping()
const mainNavigationCategories = computed<NavigationItem[]>(() => {
  return getNavigationItems(
    props.blok?.MainNavigation || [],
    props.rootCategories,
  )
})

if (mainNavigationCategories.value) {
  setFlyoutMenuCategories(mainNavigationCategories.value)
}

const activeFlyoutCategory = computed(() =>
  mainNavigationCategories.value.find(
    (cat) => cat.id === flyoutMenuCategoryId.value,
  ),
)

// Derived from the same category and the same rule the flyout itself uses, so
// the mask cannot end up covering the page with no flyout on top (ECOM-808).
const hasVisibleFlyout = computed(() => {
  if (!isFlyoutMenuOpen.value || flyoutMenuCategoryId.value === -1) {
    return false
  }

  const currentCategory = activeFlyoutCategory.value

  return hasFlyoutContent(
    findFlyoutNavigationItem(props.blok?.MainNavigation, currentCategory),
    currentCategory,
  )
})

const handleMouseEnterItem = (navigationItem: NavigationItem) => {
  closeFlyoutMenuDebounced.cancel()

  const storyblokItem = props.blok?.MainNavigation?.find(
    (item) =>
      item.RootCategoryID === navigationItem.id.toString() ||
      item.label?.toLowerCase() === navigationItem.name?.toLowerCase(),
  )

  if (
    navigationItem.children?.length > 0 ||
    hasNavigationFlyoutContent(navigationItem)
  ) {
    const enrichedNavigationItem = {
      ...navigationItem,
      NavigationItem: storyblokItem?.NavigationItem || [],
      NavigationFlyoutContent: storyblokItem?.NavigationFlyoutContent || [],
      ClickableImage: storyblokItem?.ClickableImage || [],
    }

    if (isFlyoutMenuOpen.value) {
      openFlyoutMenu(enrichedNavigationItem)
    } else {
      openFlyoutMenuDebounced(enrichedNavigationItem)
    }
  } else {
    closeFlyoutMenu()
  }
}

const handleMouseLeaveNavigation = () => {
  openFlyoutMenuDebounced.cancel()
  closeFlyoutMenuDebounced()
}

const preventClosing = closeFlyoutMenuDebounced.cancel

const forceCloseFlyout = (event: MouseEvent) => {
  openFlyoutMenuDebounced.cancel()
  closeFlyoutMenuDebounced.cancel()
  closeFlyoutMenu(event, true)
}

const { isMobile } = useDeviceType()

const isCheckoutPage = computed(() => route.fullPath.includes('checkout'))

const isScrolled = ref(false)

const handleScroll = () => {
  isScrolled.value = window.scrollY > 0
  scheduleOffsetMeasure()
}

const isHeaderMinified = computed(() => props.forceMinified || isScrolled.value)

const headerRef = ref<HTMLElement>()
const headerContentRef = ref<HTMLElement>()

// The PLP side navigation starts where this header ends, so the measurement has
// to keep up with the header condensing and the banner rewrapping (ECOM-2796).
const measureHeader = () => {
  if (headerRef.value) {
    sideNavigationOffset.value = measureChromeOffset(
      headerRef.value,
      headerContentRef.value,
    )
  }
}

// `border-box` to match the `offsetHeight` being read on both elements.
const measureBox = { box: 'border-box' } as const

// The offset is now the header's bottom edge in the viewport, which the
// non-sticky USP bar above the header shifts as the page scrolls, so re-measure
// on scroll as well — throttled to one read per frame to avoid layout thrash
// (ECOM-2796).
let offsetFrame = 0
const scheduleOffsetMeasure = () => {
  if (offsetFrame) {
    return
  }
  offsetFrame = requestAnimationFrame(() => {
    offsetFrame = 0
    measureHeader()
  })
}

useResizeObserver(headerRef, measureHeader, measureBox)
useResizeObserver(headerContentRef, measureHeader, measureBox)
watch(headerContentRef, measureHeader)

onMounted(() => {
  if (import.meta.client) {
    window.addEventListener('scroll', handleScroll, { passive: true })
  }
})

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('scroll', handleScroll)
    if (offsetFrame) {
      cancelAnimationFrame(offsetFrame)
    }
  }
})

const openSideNavigationMenu = () => {
  openSideNavigation()
}

const navigationTop = computed(() => ({
  top:
    props.stickToBanner && !isCheckoutPage.value
      ? `${bannerHeight.value}px`
      : '0px',
}))
</script>

<style lang="postcss" scoped>
.header-grid-container {
  grid-template-columns: 1fr;
  grid-template-rows: 2;
  grid-template-areas:
    'mobile'
    'desktop';
}

.mobile__navigation-container {
  @apply flex justify-between items-center
  py-[1px] mt-0
  max-h-[54px];

  grid-area: mobile;
}

.mobile__logo {
  @apply hidden lg:inline-block;
}

.mobile__burger-button-container {
  @apply lg:hidden flex justify-between items-center;
}

.mobile__burger-button-container__burger-button {
  @apply 2xl:hidden
  flex flex-col
  gap-0
  h-[3.375rem]
  mr-auto
  -ml-3
  mx-3
  px-3;
}

.mobile__burger-button-container__burger-button__text {
  @apply text-xs uppercase;
}
.desktop__top-header-meta-bar {
  @apply hidden
  lg:flex;
}
.desktop__top-header-main-menu {
  @apply ml-auto;
}
.desktop__header-container {
  @apply hidden lg:block bg-white;

  grid-area: desktop;
}
</style>
